import type { GridOptions, IRowNode, IsExternalFilterPresentParams, RowSelectionOptions } from "ag-grid-enterprise";
import type { AgGridReact } from "ag-grid-react";
import type { ArchivedRecordsSwitch } from "~/helpers/grid/archivedRecordsScope";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import archivedRecordsScope from "~/helpers/grid/archivedRecordsScope";

// Stable reference: an inline literal re-runs setGridOption each render and resets the SSRM cache.
export const DEFAULT_ROW_SELECTION = {
	copySelectedRows: true,
	mode: "multiRow" as const,
	selectAll: "filtered" as const
};

type GridRowSelection<TData> = GridOptions<TData>["rowSelection"];

interface UseGridExternalFiltersArgs<TData> {
	archivedRecordsSwitch?: ArchivedRecordsSwitch;
	gridOptions?: GridOptions;
	gridRef: React.RefObject<AgGridReact<TData>>;
	rowSelection: GridRowSelection<TData>;
	isServerSide: boolean;
	showOnlySelected: boolean;
}

export interface UseGridExternalFiltersReturn<TData> {
	doesExternalFilterPass: (node: IRowNode) => boolean;
	isExternalFilterPresent: (params: IsExternalFilterPresentParams) => boolean;
	rowSelection: GridRowSelection<TData>;
	setArchivedShown: (shown: boolean) => void;
	shownArchived: boolean;
}

const useGridExternalFilters = <TData>({
	archivedRecordsSwitch,
	gridOptions,
	gridRef,
	rowSelection: requestedRowSelection,
	isServerSide,
	showOnlySelected
}: UseGridExternalFiltersArgs<TData>): UseGridExternalFiltersReturn<TData> => {
	const [archivedShown, setArchivedShownState] = useState(false);
	const shownArchived = archivedRecordsSwitch?.shown ?? archivedShown;
	const archivedRef = useRef({ scope: archivedRecordsSwitch, shown: shownArchived });
	archivedRef.current = { scope: archivedRecordsSwitch, shown: shownArchived };

	const setArchivedShown = useCallback((shown: boolean) => {
		archivedRef.current.shown = shown;
		setArchivedShownState(shown);
	}, []);

	const hasArchivedSwitch = archivedRecordsSwitch !== undefined;
	const rowSelection = useMemo(
		() =>
			hasArchivedSwitch &&
			typeof requestedRowSelection === "object" &&
			(requestedRowSelection as RowSelectionOptions).mode === "multiRow"
				? { ...requestedRowSelection, selectAll: "filtered" as const }
				: requestedRowSelection,
		[hasArchivedSwitch, requestedRowSelection]
	) as GridRowSelection<TData>;

	useEffect(() => {
		if (archivedRef.current.scope && !isServerSide) gridRef.current?.api?.onFilterChanged();
	}, [gridRef, isServerSide, shownArchived]);

	const latestCallerExternalFilterRef = useRef<Pick<GridOptions, "doesExternalFilterPass" | "isExternalFilterPresent">>(
		{}
	);
	latestCallerExternalFilterRef.current = gridOptions ?? {};

	// Must be memoized — a new reference dispatches onFilterChanged and purges the SSRM cache.
	const isExternalFilterPresent = useCallback(
		(params: IsExternalFilterPresentParams) =>
			showOnlySelected ||
			!!archivedRef.current.scope ||
			(latestCallerExternalFilterRef.current.isExternalFilterPresent?.(params) ?? false),
		[showOnlySelected]
	);

	const doesExternalFilterPass = useCallback(
		(node: IRowNode) => {
			if (showOnlySelected && !node.isSelected()) return false;
			const { scope, shown } = archivedRef.current;
			if (
				scope &&
				node.data &&
				!archivedRecordsScope.rowPasses(node.data, gridRef.current?.api?.getFilterModel(), shown, scope)
			)
				return false;
			return latestCallerExternalFilterRef.current.doesExternalFilterPass?.(node) ?? true;
		},
		[gridRef, showOnlySelected]
	);

	return { doesExternalFilterPass, isExternalFilterPresent, rowSelection, setArchivedShown, shownArchived };
};

export default useGridExternalFilters;
